import BaseApiService  from "./BaseApiService";

class LearnerCourseService extends BaseApiService {
    getLearnerCourses = async (params = {}) => {
        const { 
            page = 1, 
            pageSize = 10, 
            limit, 
            search = "", 
            sort,
            categories,
            priceMin,
            priceMax,
            purchaseCount
        } = params;
        try {
            const response = await this.getAxiosInstance().get("/courses", {
                params: {
                    page,
                    limit: limit || pageSize,
                    search: search.trim(),
                    ...(sort ? { sort } : {}),
                    ...(categories ? { categories } : {}),
                    ...(priceMin !== undefined ? { priceMin } : {}),
                    ...(priceMax !== undefined ? { priceMax } : {})
                },
            });
            return {
                courses: response.data?.data?.courses.map(course => ({
                    ...course,
                    key: course._id
                })) || [],
                pagination: response.data?.data?.pagination || {},
            };
        } catch (error) {
            console.error("Error getting learner courses:", error);
            throw error;
        }
    };
    getLearnerCourseById = async (courseId) => {
        try {
            const response = await this.getAxiosInstance().get(`/courses/${courseId}`);
            return response.data;
        } catch (error) {
            console.error("Error getting learner course by ID:", error);
            throw error;
        }
    };
    getLearnerCourseBySlug = async (slug) => {
        try {
            const response = await this.getAxiosInstance().get(`/courses/${slug}`);
            return response.data;
        } catch (error) {
            console.error("Error getting learner course by slug:", error);
            throw error;
        }
    };
    /**
     * get comment of lesson (sort)
     * @param {string} lessonId
     * @param {string} sortBy (votingIndex | newest | userFirst)
     * @param {number} page
     * @param {number} limit
     * @returns {Promise<{comments:[], sortBy:string, pagination:object}>}
     */
    getLessonComments = async (lessonId, sortBy = 'votingIndex', page = 1, limit = 10) => {
        try {
            const response = await this.getAxiosInstance().get(`/lessons/${lessonId}/comments`, {
                params: { sortBy, page, limit },
            });
            if (!response.data?.success) throw new Error('Failed to fetch comments');
            return response.data.data;
        } catch (error) {
            console.error('Error getting lesson comments:', error);
            throw error;
        }
    };
    /**
     * save comment or reply for lesson
     * @param {string} lessonId
     * @param {string} comment
     * @param {string|null} parentCommentId
     * @returns {Promise<{comment:object}>}
     */
    postLessonComment = async (lessonId, comment, parentCommentId = null) => {
        try {
            const response = await this.getAxiosInstance().post(`/lessons/${lessonId}/comments`, {
                comment,
                parentCommentId,
            });
            if (!response.data?.success) throw new Error('Failed to post comment');
            return response.data.data;
        } catch (error) {
            console.error('Error posting lesson comment:', error);
            throw error;
        }
    };
    /**
     * Vote up/down cho comment
     * @param {string} commentId
     * @param {'upvote'|'downvote'} voteType
     * @returns {Promise<{comment:object, action:string}>}
     */
    voteLessonComment = async (commentId, voteType) => {
        try {
            const response = await this.getAxiosInstance().post(`/lessons/comments/${commentId}/vote`, { voteType });
            if (!response.data?.success) throw new Error('Failed to vote comment');
            return response.data.data;
        } catch (error) {
            console.error('Error voting lesson comment:', error);
            throw error;
        }
    };
    /**
     * Update comment
     * @param {string} commentId
     * @param {string} comment
     * @returns {Promise<{comment:object}>}
     */
    updateLessonComment = async (commentId, comment) => {
        try {
            const response = await this.getAxiosInstance().put(`/lessons/comments/${commentId}`, { comment });
            if (!response.data?.success) throw new Error('Failed to update comment');
            return response.data.data;
        } catch (error) {
            console.error('Error updating lesson comment:', error);
            throw error;
        }
    };
    /**
     * Delete comment or reply
     * @param {string} commentId
     * @returns {Promise<{message:string}>}
     */
    deleteLessonComment = async (commentId) => {
        try {
            const response = await this.getAxiosInstance().delete(`/lessons/comments/${commentId}`);
            if (!response.data?.success) throw new Error('Failed to delete comment');
            return response.data;
        } catch (error) {
            console.error('Error deleting lesson comment:', error);
            throw error;
        }
    };
    /**
     * Get total count of comments (including all nested replies) for a lesson
     * @param {string} lessonId
     * @returns {Promise<number>} totalCount
     */
    getLessonCommentCount = async (lessonId) => {
        try {
            const response = await this.getAxiosInstance().get(`/lessons/${lessonId}/comments/count`);
            if (!response.data?.success) throw new Error('Failed to fetch comment count');
            return response.data.data.totalCount;
        } catch (error) {
            console.error('Error getting lesson comment count:', error);
            throw error;
        }
    };
    /**
     * Get certificate for a completed course
     * @param {string} courseSlug
     * @returns {Promise<{certificateUrl: string}>}
     */
    getCertificate = async (courseSlug) => {
        try {
            const response = await this.getAxiosInstance().get(`/learner/courses/${courseSlug}/certificate`);
            return response.data?.data;
        } catch (error) {
            console.error('Error fetching certificate:', error);
            throw error;
        }
    };
}

export default new LearnerCourseService();